import { useLayoutEffect, useRef } from 'react';

/**
 * The underline beneath the active link of a horizontal nav. It is one element that glides
 * from the old link to the new one instead of a border that each link paints, so a tab
 * switch reads as movement. Render it as the last child of the nav's scrolling container
 * (which must be `relative`): it measures that parent and scrolls with the links.
 *
 * The active link is whichever carries aria-current="page"; `activeKey` (the pathname) tells
 * the indicator when to look again. Widths are re-measured when any link or the container
 * resizes, which also covers the web font arriving after first paint.
 *
 * The line spans the link's content (label, dot and count), not its padding, so it starts
 * where the label starts: the first tab drops its left padding to sit on the content edge, and
 * its underline lands there too.
 */
export function NavIndicator({ activeKey }: { activeKey: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const ready = useRef(false);

  useLayoutEffect(() => {
    const line = ref.current;
    const container = line?.parentElement;
    if (!line || !container) return;

    const measure = () => {
      const active = container.querySelector<HTMLElement>('[aria-current="page"]');
      if (!active) {
        line.removeAttribute('data-ready');
        ready.current = false;
        return;
      }
      const c = container.getBoundingClientRect();
      const a = active.getBoundingClientRect();
      const style = getComputedStyle(active);
      const padLeft = parseFloat(style.paddingLeft) || 0;
      const padRight = parseFloat(style.paddingRight) || 0;
      line.style.setProperty('--x', `${a.left - c.left + container.scrollLeft + padLeft}px`);
      line.style.setProperty('--w', `${a.width - padLeft - padRight}px`);
      if (!ready.current) {
        // First position: flush the style so the line is placed, then arm the transition.
        // Without the flush the line would slide in from the left edge on load.
        line.getBoundingClientRect();
        line.setAttribute('data-ready', '');
        ready.current = true;
      }
    };

    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    for (const link of container.querySelectorAll('a')) observer.observe(link);
    if (typeof document.fonts?.ready?.then === 'function') {
      void document.fonts.ready.then(measure);
    }
    return () => observer.disconnect();
  }, [activeKey]);

  return <span ref={ref} aria-hidden="true" className="nav-indicator" />;
}

/**
 * A tab label that reserves the width of its bold form, so a link that turns bold when it
 * becomes active does not nudge its neighbours.
 */
export function TabLabel({ children }: { children: string }) {
  return (
    <span className="grid">
      <span aria-hidden="true" className="col-start-1 row-start-1 invisible font-medium">
        {children}
      </span>
      <span className="col-start-1 row-start-1">{children}</span>
    </span>
  );
}
